import { AxiosResponse } from "axios";
import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { SidePanelBase, SidePanelContent, SidePanelFooter, SidePanelHeader } from "./SidePanel";

interface DetailSidePanelProps<T> {
  apiCall: () => Promise<AxiosResponse<T, any>>
  apiKey: string
  entityId: number
  title: string
  contentClassName?: string
  footerContent: JSX.Element | ((data: T) => JSX.Element)

  onOpen?: () => void
  trigger?: JSX.Element

  children: (data: T, refetch: () => Promise<void>) => JSX.Element

  // This is used when we open panenls imperatively
  skipSidePanelBase?: boolean
}

const DetailSidePanelContent = <T extends any>(props: DetailSidePanelProps<T>) => {
  const api = useApi(() => props.apiCall(), [props.apiKey, "side", props.entityId], {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [props.entityId]);

  const refetch = async () => {
    await api.refetch();
  }

  if (api.isInitialLoading) {
    return <>
      <SidePanelHeader title={props.title} />
      <SidePanelContent>
        <PlaceholderContent />
      </SidePanelContent>
      <SidePanelFooter />
    </>
  }

  return <>
    <SidePanelHeader title={props.title} />
    <SidePanelContent className={props.contentClassName}>
      {props.children(api.data!, refetch)}
    </SidePanelContent>
    <SidePanelFooter>
      {props.footerContent instanceof Function ? props.footerContent(api.data!) : props.footerContent}
    </SidePanelFooter>
  </>
}

export const DetailSidePanel = <T extends any>(props: DetailSidePanelProps<T>) => {
  if (props.skipSidePanelBase) {
    return <DetailSidePanelContent {...props} />;
  }

  return <SidePanelBase
    onOpen={props.onOpen}
    trigger={props.trigger}
    size="medium"
    id={`${props.apiKey}-side-${props.entityId}`}
  >
    <DetailSidePanelContent {...props} />
  </SidePanelBase>
}